/**
 * 微信扫码登录:前端只做发起、轮询和兑换,授权地址由后端下发。
 * 首次用微信登录的账号需要绑定手机号,后端返回 phone_binding_required 时在同一弹窗里补绑。
 */
import {apiFetch,type LoginResponse} from './api';

export interface WechatTransaction {
  transaction_id: string;
  auth_url: string;
  poll_token: string;
  expires_at: string;
}

export type WechatTransactionStatusName =
  | 'pending'
  | 'phone_binding_required'
  | 'login_ready'
  | 'binding_completed'
  | 'expired'
  | 'failed';

export interface WechatTransactionStatus {
  status: WechatTransactionStatusName;
  expires_at: string;
  next_poll_after_ms: number;
  binding_ticket?: string;
  exchange_ticket?: string;
  error?: string;
}

interface SettingsCodeResponse {
  ok: boolean;
  masked_phone: string;
  retry_after_seconds: number;
  expires_at: string;
}

export function generateClientNonce(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function startWechatTransaction(clientNonce: string, signal?: AbortSignal): Promise<WechatTransaction> {
  return apiFetch<WechatTransaction>('/v1/auth/wechat/qr/start', {
    body: { purpose: 'login', client_nonce: clientNonce },
    signal,
  });
}

export function getWechatTransactionStatus(
  transaction: Pick<WechatTransaction, 'transaction_id' | 'poll_token'>,
  clientNonce: string,
  signal?: AbortSignal,
): Promise<WechatTransactionStatus> {
  const query = new URLSearchParams({ transaction_id: transaction.transaction_id });
  return apiFetch<WechatTransactionStatus>(`/v1/auth/wechat/qr/status?${query.toString()}`, {
    headers: { 'X-WeChat-Poll-Token': transaction.poll_token, 'X-Client-Nonce': clientNonce },
    ...(signal ? { signal } : {}),
  });
}

export async function exchangeWechatLogin(
  transactionId: string,
  exchangeTicket: string,
  clientNonce: string,
  signal?: AbortSignal,
): Promise<LoginResponse> {
  return apiFetch<LoginResponse>('/v1/auth/wechat/qr/exchange', {
    body: { transaction_id: transactionId, exchange_ticket: exchangeTicket, client_nonce: clientNonce },
    signal,
  });
}

export function sendWechatPhoneCode(
  bindingTicket: string,
  clientNonce: string,
  phone: string,
  signal?: AbortSignal,
): Promise<SettingsCodeResponse> {
  return apiFetch<SettingsCodeResponse>('/v1/auth/wechat/phone/code', {
    body: { binding_ticket: bindingTicket, client_nonce: clientNonce, area_code: '86', phone },
    signal,
  });
}

export function bindWechatPhone(
  bindingTicket: string,
  clientNonce: string,
  phone: string,
  verifyCode: string,
  signal?: AbortSignal,
): Promise<{ exchange_ticket: string; expires_at: string }> {
  return apiFetch<{ exchange_ticket: string; expires_at: string }>('/v1/auth/wechat/phone/bind', {
    signal,
    body: {
      binding_ticket: bindingTicket,
      client_nonce: clientNonce,
      area_code: '86',
      phone,
      verify_code: verifyCode,
      share_code: '',
    },
  });
}
